import { css } from '@emotion/core';
import { Box, Heading2, Heading3, StackView } from 'localmed-core';
import React, { useEffect, useState } from 'react';
import { Field } from 'react-final-form';
import { FormattedMessage } from 'react-intl';
import { DEPENDENT_STATUS } from '../../../shared/components/AppointmentForm/constants';
import { isPatientCoverageInfoRequired } from '../../../shared/components/AppointmentForm/utils';
import { withCustomContexts } from '../../../shared/components/Contexts';
import { Alert, DiButton, DISelect, Input } from '../../../shared/components/DiComponents';
import { DiEmailInput } from '../../../shared/components/EmailInput';
import { DiInputAdapter, DiInputMaskAdapter } from '../../../shared/components/FinalForm';
import { DiInsuranceIssuerSelectWithData } from '../../../shared/components/InsuranceIssuerSelect';
import { trackAffiliateEvent } from '../../../shared/utils/affiliateTracking';
import getDaysInMonth from '../../../shared/utils/getDaysInMonth';
import { IWidgetSettings } from '../../../types';
import { BackLink } from '../../pages/SchedulingPage/components';
import {
  ISchedulingValues,
  IStepProps,
} from '../../pages/SchedulingPage/components/schedulingWizard';
import { isCorrectableSubmitFailure } from '../../pages/SchedulingPage/components/schedulingWizard/utils';
import {
  BackButton,
  CheckBox,
  ContentWrapper,
  DiRule,
  Label,
  Wrapper,
} from '../../pages/SchedulingPage/components/styles';
import InputError from '../InputError';
import { MONTHS_ITEMS } from './constants';
import PatientCoverageFields from './PatientCoverageFields';
import {
  constructBirthDay,
  currentYear,
  getMonth,
  infoFormCompleted,
  patientFormHasError,
} from './utils';

interface IProps extends IStepProps {
  widgetSettings: IWidgetSettings;
}

const DiInsuranceIssuerSelectWithDataAny: any = DiInsuranceIssuerSelectWithData;

function PatientInfoFormScreen({ wizard, widgetSettings }: IProps) {
  const [showInsuranceInfo, setShowInsuranceInfo] = useState<boolean>(false);
  const {
    office,
    provider,
    insuranceIssuer,
    contactEmail,
    patientBirthYear,
    patientBirthMonth,
    patientBirthDay,
    bookingForAnother,
    contactBirthYear,
    contactBirthMonth,
    contactBirthDay,
    emailValidationResult,
    patientBirthDate,
    contactBirthDate,
  } = wizard?.values || {};

  useEffect(() => {
    if (wizard?.trackSchedulingEvent) {
      wizard.trackSchedulingEvent('Patient Information Screen', 'PatientInfoFormScreen');
    }

    trackAffiliateEvent({
      aftId: widgetSettings.aftId,
      aftType: widgetSettings.aftType,
      aft: widgetSettings.aft,
      event: 'Viewed LocalMed Appointment Form',
      properties: {
        providerId: provider?.id,
        providerDisplay: provider?.display,
        officeId: office?.id,
        officeDisplay: office?.display,
      },
    });
    // the following lines make sure that the errors message shows if there any on page load.
    // eslint-disable-next-line no-unused-expressions
    if (patientBirthDate) wizard?.form.mutators.setFieldTouched('patientBirthDate', true);
    // eslint-disable-next-line no-unused-expressions
    if (contactBirthDate) wizard?.form.mutators.setFieldTouched('contactBirthDate', true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!wizard) return null;
  const { errors, submitErrors } = wizard.form.getState();

  const next = () => {
    if (isPatientCoverageInfoRequired(provider, insuranceIssuer)) {
      setShowInsuranceInfo(true);
    } else {
      wizard.gotoStep(wizard.step + 1);
    }
  };

  if (showInsuranceInfo) {
    return (
      <PatientCoverageFields
        wizard={wizard}
        close={() => {
          setShowInsuranceInfo(false);
        }}
      />
    );
  }

  const setBirthDateField = (
    day: string | undefined,
    month: string | undefined,
    year: string | undefined,
    field: keyof ISchedulingValues = 'patientBirthDate'
  ) => {
    if (day && month && year) {
      wizard.form.change(field, constructBirthDay(day, month, year));
      wizard.form.mutators.setFieldTouched(field, true);
    }
  };
  return (
    <Wrapper>
      <BackLink
        onClick={() => {
          wizard.gotoStep(wizard.step - 1);
        }}
      />
      <form>
        <ContentWrapper>
          {submitErrors && isCorrectableSubmitFailure(submitErrors) && (
            <Alert type="error" scrollIntoView marginBottom="md">
              <FormattedMessage
                id="scheduling.PatientInfoFormScreen.validationError"
                defaultMessage="Please correct the errors in the form and submit again."
              />
            </Alert>
          )}
          <StackView spacing="lg" width="100%">
            <Heading2 mb="sm" textAlign={{ _: 'center', lg: 'left' }}>
              <FormattedMessage
                id="scheduling.PatientInfoFormScreen.titleMessage"
                defaultMessage="Let’s grab some of your info"
              />
            </Heading2>
            <Box>
              <Heading3 mb="sm">
                <FormattedMessage
                  id="scheduling.PatientInfoFormScreen.PatientInfoMessage"
                  defaultMessage="Patient Info"
                />
              </Heading3>
              <Label id="name" htmlFor="firstName" mb="sm" required>
                <FormattedMessage
                  id="scheduling.PatientInfoFormScreen.patientNameInputsLabel"
                  defaultMessage="Patient Name (Required)"
                />
              </Label>
              <StackView spacing="sm" direction="row">
                <Field
                  name="patientGivenName"
                  flex="1 0 0"
                  id="firstName"
                  placeholder="First Name"
                  autoComplete="given-name"
                  component={DiInputAdapter}
                />

                <Field
                  name="patientFamilyName"
                  flex="1 0 0"
                  id="lastName"
                  placeholder="Last Name"
                  autoComplete="family-name"
                  component={DiInputAdapter}
                  aria-labelledby="name"
                />
              </StackView>
              <InputError form={wizard.form} field="patientGivenName" />
              <InputError form={wizard.form} field="patientFamilyName" />
            </Box>
            <Box>
              <Label
                id="patientBirthMonth-label"
                htmlFor="patientBirthMonth-input"
                mb="sm"
                required
              >
                <FormattedMessage
                  id="scheduling.PatientInfoFormScreen.patientBirthDateLabel"
                  defaultMessage="Patient Date of Birth (Required)"
                />
              </Label>
              <Field name="patientBirthDate">
                {() => (
                  <StackView spacing="sm" direction="row" mb="md">
                    <DISelect
                      id="patientBirthMonth"
                      name="patientBirthMonth"
                      aria-labelledby="patientBirthMonth-label"
                      placeholder="January"
                      autoComplete="bday-month"
                      selectedItem={getMonth(patientBirthMonth)}
                      items={MONTHS_ITEMS}
                      onChange={(value) => {
                        wizard.form.change('patientBirthMonth', value.id);
                        setBirthDateField(patientBirthDay, value.id, patientBirthYear);
                      }}
                    />
                    <Input
                      type="text"
                      min={1}
                      max={getDaysInMonth(patientBirthMonth, patientBirthYear)}
                      inputMode="numeric"
                      pattern="[0-9]{2}"
                      maxLength={2}
                      id="patientBirthDay"
                      name="patientBirthDay"
                      aria-labelledby="patientBirthMonth-label"
                      flex="1 0 0"
                      placeholder="DD"
                      autoComplete="bday-day"
                      value={patientBirthDay}
                      onChange={({ target: { value } }) => {
                        wizard.form.change('patientBirthDay', value);
                      }}
                      onBlur={() => {
                        setBirthDateField(patientBirthDay, patientBirthMonth, patientBirthYear);
                      }}
                    />

                    <Input
                      type="text"
                      min={currentYear - 120}
                      max={currentYear}
                      inputMode="numeric"
                      pattern="[0-9]{4}"
                      maxLength={4}
                      id="patientBirthYear"
                      name="patientBirthYear"
                      aria-labelledby="patientBirthMonth-label"
                      flex="1 0 0"
                      placeholder="YYYY"
                      autoComplete="bday-year"
                      value={patientBirthYear}
                      onChange={(event) => {
                        wizard.form.change('patientBirthYear', event.target.value);
                      }}
                      onBlur={() => {
                        setBirthDateField(patientBirthDay, patientBirthMonth, patientBirthYear);
                        wizard.form.mutators.setFieldTouched('patientBirthDate', true);
                      }}
                    />
                  </StackView>
                )}
              </Field>
              <InputError form={wizard.form} field="patientBirthDate" />
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="other">
                <CheckBox
                  type="checkbox"
                  id="other"
                  name="other"
                  checked={bookingForAnother}
                  onChange={(event) => {
                    wizard.form.batch(() => {
                      wizard.form.change('bookingForAnother', !!event.target.checked);
                      wizard.form.change(
                        'dependentStatus',
                        event.target.checked ? DEPENDENT_STATUS.DEPENDENT : DEPENDENT_STATUS.ME
                      );
                    });
                  }}
                />
                <FormattedMessage
                  id="scheduling.InitialScreen.scheduleForOtherLabelMessage"
                  defaultMessage="I’m booking for someone else"
                />
              </label>
            </Box>
            <DiRule />
            <Heading3>
              <FormattedMessage
                id="scheduling.PatientInfoFormScreen.ContactInfoMessage"
                defaultMessage="Contact Info"
              />
            </Heading3>
            {bookingForAnother && (
              <StackView spacing="md">
                <Box>
                  <Label id="contactName" htmlFor="contactGivenName" mb="sm" required>
                    <FormattedMessage
                      id="scheduling.PatientInfoFormScreen.contactNameInputsLabel"
                      defaultMessage="Your Name (Required)"
                    />
                  </Label>
                  <StackView spacing="sm" direction="row">
                    <Field
                      name="contactGivenName"
                      flex="1 0 0"
                      id="contactGivenName"
                      placeholder="First Name"
                      autoComplete="given-name"
                      aria-labelledby="contactName"
                      component={DiInputAdapter}
                    />

                    <Field
                      name="contactFamilyName"
                      flex="1 0 0"
                      id="contactFamilyName"
                      placeholder="Last Name"
                      autoComplete="family-name"
                      aria-labelledby="contactName"
                      component={DiInputAdapter}
                    />
                  </StackView>
                  <InputError form={wizard.form} field="contactGivenName" />
                  <InputError form={wizard.form} field="contactFamilyName" />
                </Box>
                <Box>
                  <Label
                    id="contactBirthMonth-label"
                    htmlFor="contactBirthMonth-input"
                    mb="sm"
                    required
                  >
                    <FormattedMessage
                      id="scheduling.PatientInfoFormScreen.contactBirthDateLabel"
                      defaultMessage="Your Date of Birth (Required)"
                    />
                  </Label>
                  <Field name="contactBirthDate">
                    {() => (
                      <StackView spacing="sm" direction="row">
                        <DISelect
                          id="contactBirthMonth"
                          name="contactBirthMonth"
                          aria-labelledby="contactBirthMonth-label"
                          placeholder="January"
                          autoComplete="bday-month"
                          selectedItem={getMonth(contactBirthMonth)}
                          items={MONTHS_ITEMS}
                          onChange={(value) => {
                            wizard.form.batch(() => {
                              wizard.form.change('contactBirthMonth', value.id);
                              setBirthDateField(
                                contactBirthDay,
                                value.id,
                                contactBirthYear,
                                'contactBirthDate'
                              );
                            });
                          }}
                        />
                        <Input
                          type="text"
                          min={1}
                          max={getDaysInMonth(contactBirthMonth, contactBirthYear)}
                          inputMode="numeric"
                          pattern="[0-9]{2}"
                          maxLength={2}
                          id="contactBirthDay"
                          name="contactBirthDay"
                          aria-labelledby="contactBirthMonth-label"
                          flex="1 0 0"
                          placeholder="DD"
                          autoComplete="bday-day"
                          value={contactBirthDay}
                          onChange={({ target: { value } }) => {
                            wizard.form.change('contactBirthDay', value);
                          }}
                          onBlur={() => {
                            setBirthDateField(
                              contactBirthDay,
                              contactBirthMonth,
                              contactBirthYear,
                              'contactBirthDate'
                            );
                          }}
                        />

                        <Input
                          type="text"
                          min={currentYear - 120}
                          max={currentYear}
                          inputMode="numeric"
                          pattern="[0-9]{4}"
                          maxLength={4}
                          id="contactBirthYear"
                          name="contactBirthYear"
                          aria-labelledby="contactBirthMonth-label"
                          flex="1 0 0"
                          placeholder="YYYY"
                          autoComplete="bday-year"
                          value={contactBirthYear}
                          onChange={(event) => {
                            wizard.form.change('contactBirthYear', event.target.value);
                          }}
                          onBlur={() => {
                            setBirthDateField(
                              contactBirthDay,
                              contactBirthMonth,
                              contactBirthYear,
                              'contactBirthDate'
                            );
                            wizard.form.mutators.setFieldTouched('contactBirthDate', true);
                          }}
                        />
                      </StackView>
                    )}
                  </Field>
                </Box>
                <InputError form={wizard.form} field="contactBirthDate" />
              </StackView>
            )}
            <Box>
              <Label htmlFor="contactEmail" mb="sm" required>
                <FormattedMessage
                  id="scheduling.PatientInfoFormScreen.patientNameInputsLabel"
                  defaultMessage="Email (Required)"
                />
              </Label>
              <Field name="contactEmail">
                {() => (
                  <DiEmailInput
                    id="contactEmail"
                    name="contactEmail"
                    flex="1 0 0"
                    placeholder=""
                    autoComplete="email"
                    value={contactEmail}
                    validationResult={emailValidationResult}
                    onChange={(event) => {
                      wizard.form.change('contactEmail', event.target.value);
                    }}
                    onBlur={() => {
                      wizard.form.mutators.setFieldTouched('contactEmail', true);
                    }}
                    onValidate={(validationResult) =>
                      wizard.form.change('contactEmail', validationResult)
                    }
                  />
                )}
              </Field>
              <InputError form={wizard.form} field="contactEmail" />
            </Box>
            <Box>
              <Label htmlFor="contactMobilePhone" mb="sm" required>
                <FormattedMessage
                  id="scheduling.PatientInfoFormScreen.patientPhoneNumberLabel"
                  defaultMessage="Cell Phone Number (Required)"
                />
              </Label>
              <Field
                name="contactMobilePhone"
                mask="111-111-1111"
                id="contactMobilePhone"
                flex="1 0 0"
                placeholder="###-###-####"
                autoComplete="tel"
                component={DiInputMaskAdapter}
              />
              <InputError
                form={wizard.form}
                field="contactMobilePhone"
                submitField="contactPhone"
              />
            </Box>
            {office?.insuranceCoverageEnabled && (
              <Box>
                <Label id="insuranceIssuer-label" htmlFor="insuranceIssuer-input" mb="sm" required>
                  <FormattedMessage
                    id="scheduling.PatientInfoFormScreen.patientInsuranceLabel"
                    defaultMessage="Payment Method (Required)"
                  />
                </Label>
                <Field name="providerInsuranceCoverage">
                  {() => (
                    <DiInsuranceIssuerSelectWithDataAny
                      id="insuranceIssuer"
                      emptyLabel="Other"
                      placeholder="Select One"
                      providerId={provider && provider.id}
                      selectedItem={insuranceIssuer}
                      initialSelectedValue={insuranceIssuer && insuranceIssuer.id}
                      countryCode={office.countryCode?.toLowerCase() || 'us'}
                      onChange={(selectedItem) => {
                        wizard.form.change('insuranceIssuer', selectedItem);
                      }}
                      onBlur={() => {
                        wizard.form.mutators.setFieldTouched('insuranceIssuer', true);
                      }}
                      onProviderInsuranceCoverageChange={(providerInsurance) => {
                        wizard.form.change('providerInsuranceCoverage', providerInsurance);
                      }}
                    />
                  )}
                </Field>
                <InputError form={wizard.form} field="providerInsuranceCoverage" />
              </Box>
            )}
            <Box>
              <Label htmlFor="notes" mb="sm">
                <FormattedMessage
                  id="scheduling.PatientInfoFormScreen.patientNotesLabel"
                  defaultMessage="Message for the Office"
                />
              </Label>
              <Field
                multiline
                rows="4"
                id="notes"
                name="notes"
                flex="1 0 0"
                placeholder="Message"
                component={DiInputAdapter}
                css={css`
                  resize: vertical;
                `}
              />
            </Box>
          </StackView>

          <Box
            display="flex"
            justifyContent={{ _: 'center', lg: 'space-between' }}
            width="100%"
            my="lg"
          >
            <BackButton
              onClick={() => {
                wizard.gotoStep(wizard.step - 1);
              }}
              display={{ _: 'none', lg: 'inherit' }}
              variant="subtle"
              size="xl"
            >
              <FormattedMessage
                id="scheduling.PatientInfoFormScreen.cancelButton"
                defaultMessage="Back"
              />
            </BackButton>
            <DiButton
              onClick={next}
              size="xl"
              disabled={!infoFormCompleted(wizard.values) || patientFormHasError(errors)}
            >
              <FormattedMessage
                id="scheduling.PatientInfoFormScreen.NextButton"
                defaultMessage="Next"
              />
            </DiButton>
          </Box>
        </ContentWrapper>
      </form>
    </Wrapper>
  );
}

PatientInfoFormScreen.defaultProps = {
  // eslint-disable-next-line react/default-props-match-prop-types
  id: 'PATIENT_INFO_FORM_SCREEN',
};

export default withCustomContexts(PatientInfoFormScreen);
